import React, { useCallback, useEffect, useState } from 'react';
import { Plug } from 'lucide-react';
import {
  createKey, createWebhook, deleteWebhook, listDeliveries, listKeys, listWebhooks, revokeKey, testWebhook, updateWebhook,
  type ApiKeyRow, type DeliveryRow, type WebhookRow,
} from '../../api/integrationsApi';

/** Integrations (SRS #129/#130): API keys for CRM/ERP middleware, outbound webhooks, and the docs to use them. */
const API_URL = process.env.REACT_APP_API_BASE_URL || window.location.origin;

const IntegrationsPage: React.FC = () => {
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [scopes, setScopes] = useState<string[]>([]);
  const [hooks, setHooks] = useState<WebhookRow[]>([]);
  const [events, setEvents] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reveal, setReveal] = useState<{ value: string; label: string } | null>(null);
  const [keyForm, setKeyForm] = useState({ name: '', scopes: [] as string[], expires: '' });
  const [hookForm, setHookForm] = useState({ url: '', description: '', events: [] as string[] });
  const [deliveries, setDeliveries] = useState<{ id: number; rows: DeliveryRow[] } | null>(null);
  const [gated, setGated] = useState<string | null>(null);

  const load = useCallback(() => {
    Promise.all([listKeys(), listWebhooks()]).then(([k, w]) => {
      setKeys(k.keys); setScopes(k.scopes); setKeyForm((f) => ({ ...f, scopes: f.scopes.length ? f.scopes : k.scopes }));
      setHooks(w.webhooks); setEvents(w.events);
    }).catch((e: Error & { status?: number }) => { if (e.status === 403) setGated(e.message); else setError(e.message); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const muted = 'var(--color-text-muted, var(--ds-text-muted))';
  const text = 'var(--color-text, var(--ds-text))';
  const border = 'var(--ds-border, var(--ds-border))';
  const card: React.CSSProperties = { border: `1px solid ${border}`, borderRadius: 14, padding: 18, background: 'var(--ds-surface, #fff)', marginBottom: 18 };
  const input: React.CSSProperties = { padding: '6px 8px', borderRadius: 6, border: `1px solid ${border}`, fontSize: 13, background: 'var(--ds-surface, #fff)', color: text };
  const btn: React.CSSProperties = { padding: '6px 12px', borderRadius: 6, border: `1px solid ${border}`, background: 'var(--ds-surface, #fff)', cursor: 'pointer', fontSize: 13, color: text };
  const primary: React.CSSProperties = { ...btn, background: 'var(--ds-brand-blue)', color: 'var(--ds-text-on-dark)', border: 'none', fontWeight: 600 };
  const code: React.CSSProperties = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12, background: 'rgba(0,0,0,0.04)', padding: '8px 10px', borderRadius: 6, overflowX: 'auto', whiteSpace: 'pre' };
  const td: React.CSSProperties = { padding: '6px 8px', borderBottom: `1px solid ${border}`, color: text };

  if (gated) {
    return (
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '40px 20px', textAlign: 'center' }}>
        <Plug size={28} aria-hidden />
        <h1 style={{ fontSize: 22, margin: '10px 0 6px' }}>Integrations</h1>
        <p style={{ color: muted }}>{gated}</p>
        <a href="/pricing" style={primary}>See plans with API access</a>
      </div>
    );
  }

  const onCreateKey = async (e: React.FormEvent) => {
    e.preventDefault(); setError(null);
    try {
      const k = await createKey({ name: keyForm.name, scopes: keyForm.scopes, expires_days: keyForm.expires ? Number(keyForm.expires) : null });
      setReveal({ value: k.key || '', label: 'API key' }); setKeyForm((f) => ({ ...f, name: '' })); load();
    } catch (err) { setError(err instanceof Error ? err.message : 'Failed'); }
  };
  const onCreateHook = async (e: React.FormEvent) => {
    e.preventDefault(); setError(null);
    try {
      const w = await createWebhook({ url: hookForm.url, events: hookForm.events, description: hookForm.description || undefined });
      setReveal({ value: w.secret || '', label: 'signing secret' }); setHookForm({ url: '', description: '', events: [] }); load();
    } catch (err) { setError(err instanceof Error ? err.message : 'Failed'); }
  };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '28px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <Plug size={22} aria-hidden />
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: text }}>Integrations</h1>
      </div>
      <p style={{ margin: '0 0 20px', color: muted, fontSize: 14 }}>Connect your CRM, ERP or middleware: read regulations, alerts, assessments, team and contracts through the Integration API, and receive events by webhook.</p>
      {error && <div style={{ color: 'var(--ds-danger-text)', fontSize: 13, marginBottom: 12 }}>{error}</div>}

      {reveal && (
        <div style={{ ...card, borderColor: '#fedf89', background: 'var(--ds-warning-bg)' }}>
          <div style={{ fontWeight: 700, color: 'var(--ds-warning-text)' }}>Copy your {reveal.label} now — it is shown only once.</div>
          <div style={{ ...code, marginTop: 8, background: 'var(--ds-surface)' }}>{reveal.value}</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" style={btn} onClick={() => navigator.clipboard?.writeText(reveal.value)}>Copy</button>
            <button type="button" style={btn} onClick={() => setReveal(null)}>I have saved it</button>
          </div>
        </div>
      )}

      <section style={card}>
        <div style={{ fontSize: 16, fontWeight: 800, color: text }}>API keys</div>
        <div style={{ fontSize: 12.5, color: muted, marginBottom: 12 }}>Send as <code>Authorization: Bearer po_live_…</code> or <code>X-API-Key</code>. 600 requests per minute per key. Keys belong to the workspace and are recorded in the activity history.</div>
        <form onSubmit={onCreateKey} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <input style={{ ...input, minWidth: 180 }} required placeholder="Key name (e.g. Salesforce sync)" value={keyForm.name} onChange={(e) => setKeyForm({ ...keyForm, name: e.target.value })} />
          {scopes.map((s) => (
            <label key={s} style={{ fontSize: 12.5, color: text }}><input type="checkbox" checked={keyForm.scopes.includes(s)} onChange={(e) => setKeyForm({ ...keyForm, scopes: e.target.checked ? [...keyForm.scopes, s] : keyForm.scopes.filter((x) => x !== s) })} /> {s}</label>
          ))}
          <select style={input} value={keyForm.expires} onChange={(e) => setKeyForm({ ...keyForm, expires: e.target.value })}>
            <option value="">Never expires</option><option value="90">90 days</option><option value="365">1 year</option>
          </select>
          <button type="submit" style={primary}>Create key</button>
        </form>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead><tr style={{ color: muted, textAlign: 'left', fontSize: 11, textTransform: 'uppercase' }}>{['Name', 'Key', 'Scopes', 'Last used', 'Calls', ''].map((h) => <th key={h} style={{ padding: '6px 8px', borderBottom: `1px solid ${border}` }}>{h}</th>)}</tr></thead>
          <tbody>
            {keys.length === 0 && <tr><td colSpan={6} style={{ padding: 10, color: muted }}>No keys yet.</td></tr>}
            {keys.map((k) => (
              <tr key={k.id} style={{ opacity: k.revoked_at ? 0.5 : 1 }}>
                <td style={td}>{k.name}{k.revoked_at && <span style={{ color: muted }}> · revoked</span>}{k.expires_at && <div style={{ fontSize: 11, color: muted }}>expires {new Date(k.expires_at).toLocaleDateString()}</div>}</td>
                <td style={{ ...td, fontFamily: 'ui-monospace, monospace' }}>{k.prefix}</td>
                <td style={{ ...td, color: muted }}>{k.scopes.join(', ')}</td>
                <td style={{ ...td, color: muted }}>{k.last_used_at ? `${new Date(k.last_used_at).toLocaleString()}${k.last_used_ip ? ` · ${k.last_used_ip}` : ''}` : 'never'}</td>
                <td style={td}>{k.calls}</td>
                <td style={td}>{!k.revoked_at && <button type="button" style={{ ...btn, color: 'var(--ds-danger-text)' }} onClick={async () => { if (window.confirm(`Revoke "${k.name}"? Anything using it stops working immediately.`)) { await revokeKey(k.id).catch((e) => setError(e.message)); load(); } }}>Revoke</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section style={card}>
        <div style={{ fontSize: 16, fontWeight: 800, color: text }}>Webhooks</div>
        <div style={{ fontSize: 12.5, color: muted, marginBottom: 12 }}>HTTPS endpoints that receive events as signed JSON (<code>X-PolicyOra-Signature: t=…,v1=HMAC-SHA256(secret, t + "." + body)</code>). Failed deliveries retry after 1 min, 5 min, 30 min, 2 h and 6 h; an endpoint that fails ten times in a row is switched off.</div>
        <form onSubmit={onCreateHook} style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input style={{ ...input, flex: 2, minWidth: 260 }} required placeholder="https://your-system.example/policyora" value={hookForm.url} onChange={(e) => setHookForm({ ...hookForm, url: e.target.value })} />
            <input style={{ ...input, flex: 1, minWidth: 160 }} placeholder="Description (e.g. HubSpot)" value={hookForm.description} onChange={(e) => setHookForm({ ...hookForm, description: e.target.value })} />
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {events.map((ev) => <label key={ev} style={{ fontSize: 12.5, color: text }}><input type="checkbox" checked={hookForm.events.includes(ev)} onChange={(e) => setHookForm({ ...hookForm, events: e.target.checked ? [...hookForm.events, ev] : hookForm.events.filter((x) => x !== ev) })} /> {ev}</label>)}
          </div>
          <div><button type="submit" style={primary} disabled={!hookForm.events.length}>Add endpoint</button></div>
        </form>
        {hooks.length === 0 && <div style={{ color: muted, fontSize: 13 }}>No endpoints yet.</div>}
        {hooks.map((w) => (
          <div key={w.id} style={{ border: `1px solid ${border}`, borderRadius: 10, padding: '10px 12px', marginBottom: 8, opacity: w.active ? 1 : 0.6 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <code style={{ fontSize: 12.5, color: text }}>{w.url}</code>
              {w.description && <span style={{ fontSize: 12.5, color: muted }}>{w.description}</span>}
              <span style={{ marginLeft: 'auto', fontSize: 11.5, color: w.last_status === 'failed' ? 'var(--ds-danger)' : muted }}>{w.active ? '' : 'inactive · '}{w.last_status ? `last: ${w.last_status}` : 'no deliveries yet'}{w.consecutive_failures > 0 ? ` · ${w.consecutive_failures} failures` : ''}</span>
            </div>
            <div style={{ fontSize: 12, color: muted, marginTop: 4 }}>{w.events.join(', ')}</div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              <button type="button" style={btn} onClick={() => testWebhook(w.id).then(() => setError(null)).catch((e) => setError(e.message))}>Send test</button>
              <button type="button" style={btn} onClick={async () => { const r = await listDeliveries(w.id).catch(() => ({ deliveries: [] as DeliveryRow[] })); setDeliveries({ id: w.id, rows: r.deliveries }); }}>Deliveries</button>
              <button type="button" style={btn} onClick={async () => { await updateWebhook(w.id, { active: !w.active }).catch((e) => setError(e.message)); load(); }}>{w.active ? 'Disable' : 'Enable'}</button>
              <button type="button" style={{ ...btn, color: 'var(--ds-danger-text)' }} onClick={async () => { if (window.confirm('Remove this endpoint?')) { await deleteWebhook(w.id).catch((e) => setError(e.message)); load(); } }}>Remove</button>
            </div>
            {deliveries?.id === w.id && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 8 }}>
                <thead><tr style={{ color: muted, textAlign: 'left' }}>{['When', 'Event', 'Status', 'Attempts', 'Response'].map((h) => <th key={h} style={{ padding: '4px 6px', borderBottom: `1px solid ${border}` }}>{h}</th>)}</tr></thead>
                <tbody>
                  {deliveries.rows.length === 0 && <tr><td colSpan={5} style={{ padding: 6, color: muted }}>None yet.</td></tr>}
                  {deliveries.rows.map((d) => (
                    <tr key={d.id}>
                      <td style={{ padding: '4px 6px', color: muted }}>{d.created_at ? new Date(d.created_at).toLocaleString() : ''}</td>
                      <td style={{ padding: '4px 6px', color: text }}>{d.event}</td>
                      <td style={{ padding: '4px 6px', color: d.status === 'sent' ? 'var(--ds-success)' : d.status === 'failed' ? 'var(--ds-danger)' : 'var(--ds-warning)' }}>{d.status}{d.next_attempt_at && d.status === 'pending' ? ` · retry ${new Date(d.next_attempt_at).toLocaleTimeString()}` : ''}</td>
                      <td style={{ padding: '4px 6px', color: text }}>{d.attempts}</td>
                      <td style={{ padding: '4px 6px', color: muted }}>{d.response_code ?? ''} {d.error ? String(d.error).slice(0, 80) : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </section>

      <section style={card}>
        <div style={{ fontSize: 16, fontWeight: 800, color: text }}>Zapier, n8n and Make</div>
        <div style={{ fontSize: 12.5, color: muted, marginBottom: 10 }}>No connector to install: both tools subscribe a URL to an event with your API key (a REST hook), receive the signed JSON, and unsubscribe when the workflow is switched off. Create a key with the <code>manage:hooks</code> scope above.</div>
        <div style={code}>{`# Zapier — "Webhooks by Zapier" or a private app trigger (REST Hook)
Subscribe:   POST ${API_URL}/api/v1/integration/hooks
             Authorization: Bearer po_live_…    body: {"event": "alert.match", "target_url": "{{bundle.targetUrl}}", "label": "Zapier: Slack"}
Unsubscribe: DELETE ${API_URL}/api/v1/integration/hooks/{{bundle.subscribeData.id}}
Perform list (sample fields): GET ${API_URL}/api/v1/integration/hooks/samples/alert.match

# n8n — Webhook node (POST) as the trigger; then an HTTP Request node once to subscribe it:
POST ${API_URL}/api/v1/integration/hooks  {"event": "regulation.changed", "target_url": "<your n8n webhook URL>", "label": "n8n: CRM sync"}
Verify each delivery in a Code node with the secret returned on subscribe (X-PolicyOra-Signature, see below).

Events: regulation.new · regulation.changed · alert.match · assessment.complete · contract.reminder · subscription.renewed · subscription.expired · team.member.added`}</div>
      </section>

      <section style={card}>
        <div style={{ fontSize: 16, fontWeight: 800, color: text }}>Using the Integration API</div>
        <div style={{ fontSize: 12.5, color: muted, marginBottom: 10 }}>Full reference with every parameter: <a href={`${API_URL}/docs#tag/Integration-API`} target="_blank" rel="noreferrer">OpenAPI docs → Integration API</a>. Everything is read-only and scoped to your workspace's jurisdictions exactly as in the product.</div>
        <div style={code}>{`# Regulations in scope, newest first (paginate with offset)
curl -H "Authorization: Bearer po_live_…" \\
  "${API_URL}/api/v1/integration/regulations?jurisdiction_code=EU&since=2026-01-01&limit=100"

# Alert rules and their recent matches
curl -H "X-API-Key: po_live_…" "${API_URL}/api/v1/integration/alerts?matches_since=2026-09-01T00:00:00Z"

# Completed assessments (governance | risk | compliance)
curl -H "X-API-Key: po_live_…" "${API_URL}/api/v1/integration/assessments?engine=risk"

# Team members, contracts, and the webhook event catalogue
GET /api/v1/integration/team/members   GET /api/v1/integration/contracts   GET /api/v1/integration/events`}</div>
        <div style={{ ...code, marginTop: 8 }}>{`# Verify a webhook (Python)
import hmac, hashlib
parts = dict(p.split("=", 1) for p in headers["X-PolicyOra-Signature"].split(","))
expected = hmac.new(SECRET.encode(), f"{parts['t']}.".encode() + raw_body, hashlib.sha256).hexdigest()
ok = hmac.compare_digest(expected, parts["v1"])`}</div>
      </section>
    </div>
  );
};

export default IntegrationsPage;
